import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Validate stored credentials against the backend
  const validateCredentials = useCallback(async (creds) => {
    try {
      const token = btoa(`${creds.username}:${creds.password}`);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, {
        headers: { Authorization: `Basic ${token}` }
      });
      return response.ok;
    } catch (error) {
      console.error('Credential validation failed:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const savedCreds = localStorage.getItem('admin_creds');
      if (savedCreds) {
        const creds = JSON.parse(savedCreds);
        // Validate credentials are still valid
        const isValid = await validateCredentials(creds);
        if (isValid) {
          setCredentials(creds);
          setIsAuthenticated(true);
          setUserRole(creds.role || 'admin');
        } else {
          // Clear invalid credentials
          localStorage.removeItem('admin_creds');
        }
      }
      setIsLoading(false);
    };
    
    initAuth();
  }, [validateCredentials]);

  const login = (username, password, role = 'admin') => {
    const creds = { username, password, role };
    localStorage.setItem('admin_creds', JSON.stringify(creds));
    setCredentials(creds);
    setUserRole(role);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem('admin_creds');
    setCredentials(null);
    setUserRole(null);
    setIsAuthenticated(false);
  };

  const getAuthHeader = () => {
    if (!credentials) return {};
    const token = btoa(`${credentials.username}:${credentials.password}`);
    return { Authorization: `Basic ${token}` };
  };

  const isSuperAdmin = () => {
    return userRole === 'super_admin' || credentials?.username === 'admin';
  };

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <AdminAuthContext.Provider value={{ 
      isAuthenticated, 
      login, 
      logout, 
      getAuthHeader, 
      userRole,
      isSuperAdmin,
      username: credentials?.username 
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
};